import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Pagination,
  CircularProgress,
  Grid,
  TableSortLabel,
  Collapse,
  Card,
  CardContent,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import ptBR from 'date-fns/locale/pt-BR';
import api from '../../services/api';
import Layout from '../Layout';
import CargaEmpresasDialog from './CargaEmpresasDialog';

const EmpresasPanel = () => {
  const [empresas, setEmpresas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [tiposAtendimento, setTiposAtendimento] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCargaDialog, setOpenCargaDialog] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState(null);

  const isAdmin = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return !!u.is_admin;
    } catch {
      return false;
    }
  })();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [paginacao, setPaginacao] = useState({
    pagina_atual: 1,
    total_paginas: 1,
    total_registros: 0,
    registros_por_pagina: 50
  });
  const [filtros, setFiltros] = useState({
    nomeFantasia: '',
    codigoAgendor: '',
    categoria: '',
    tipoAtendimento: '',
    dataCadastro_de: null,
    dataCadastro_ate: null
  });
  const [ordenacao, setOrdenacao] = useState({
    order_by: 'ID',
    order_direction: 'asc'
  });
  const [formData, setFormData] = useState({
    NomeFantasia: '',
    CodigoEmpresa: '',
    Categoria: '',
    TipoAtendimento: '',
    categoriaSelecionada: null,
    tipoAtendimentoSelecionado: null,
  });

  const fetchEmpresas = useCallback(async (page = 1, customOrdenacao = null) => {
    try {
      setLoading(true);
      
      // Usar ordenação customizada ou a atual
      const ordenacaoAtual = customOrdenacao || ordenacao;
      
      // Por enquanto, vamos usar apenas a rota simples sem filtros
      let response;
      try {
        response = await api.get('/empresas');
      } catch (adminError) {
        // Se a rota admin não existir, usar a rota simples
        response = await api.get('/empresas');
      }
      
      console.log('Resposta da API de empresas:', response.data);
      
      // Verificar se a resposta tem estrutura de paginação
      if (response.data.dados && response.data.paginacao) {
        setEmpresas(response.data.dados);
        setPaginacao(response.data.paginacao);
      } else {
        // Fallback para formato simples (sem paginação)
        let dados = [];
        if (Array.isArray(response.data)) {
          dados = response.data;
        } else if (response.data && response.data.empresas) {
          dados = response.data.empresas;
        } else if (response.data && typeof response.data === 'object') {
          const possibleArray = Object.values(response.data).find(value => Array.isArray(value));
          if (possibleArray) {
            dados = possibleArray;
          }
        }
        
        // Aplicar filtros localmente se não houver paginação no backend
        if (filtros.nomeFantasia) {
          dados = dados.filter(item => 
            item['Nome Fantasia'] && item['Nome Fantasia'].toLowerCase().includes(filtros.nomeFantasia.toLowerCase())
          );
        }
        if (filtros.categoria) {
          dados = dados.filter(item => 
            item.Categoria && item.Categoria.toLowerCase().includes(filtros.categoria.toLowerCase())
          );
        }
        if (filtros.codigoAgendor) {
          const termo = filtros.codigoAgendor.toString();
          dados = dados.filter(item => {
            const codigo = (item.CodigoEmpresa ?? item.codigo_agendor ?? '').toString();
            return codigo.includes(termo);
          });
        }
        
        // Aplicar ordenação localmente
        dados.sort((a, b) => {
          let aValue, bValue;
          switch (ordenacaoAtual.order_by) {
            case 'ID':
              aValue = a.ID;
              bValue = b.ID;
              break;
            case 'NomeFantasia':
              aValue = a['Nome Fantasia'] || '';
              bValue = b['Nome Fantasia'] || '';
              break;
            case 'Categoria':
              aValue = a.Categoria || '';
              bValue = b.Categoria || '';
              break;
            default:
              aValue = a.ID;
              bValue = b.ID;
          }
          
          if (ordenacaoAtual.order_direction === 'desc') {
            return bValue > aValue ? 1 : -1;
          } else {
            return aValue > bValue ? 1 : -1;
          }
        });
        
        // Simular paginação local
        const totalRegistros = dados.length;
        const registrosPorPagina = 50;
        const totalPaginas = Math.ceil(totalRegistros / registrosPorPagina);
        const inicio = (page - 1) * registrosPorPagina;
        const fim = inicio + registrosPorPagina;
        const dadosPaginados = dados.slice(inicio, fim);
        
        setEmpresas(dadosPaginados);
        setPaginacao({
          pagina_atual: page,
          total_paginas: totalPaginas,
          total_registros: totalRegistros,
          registros_por_pagina: registrosPorPagina
        });
      }
    } catch (error) {
      console.error('Erro ao buscar empresas:', error.response || error);
      setError('Erro ao carregar empresas. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filtros, ordenacao]);

  // Buscar metadados completos quando necessário (para nova empresa)
  const fetchMetadataCompleta = useCallback(async () => {
    try {
      const [categoriasResponse, tiposResponse] = await Promise.all([
        api.get('/categorias'),
        api.get('/tipos-atendimento')
      ]);
      
      // Tratar estrutura de dados (pode ser array direto ou paginado)
      let categoriasData = [];
      if (Array.isArray(categoriasResponse.data)) {
        categoriasData = categoriasResponse.data;
      } else if (categoriasResponse.data.dados && Array.isArray(categoriasResponse.data.dados)) {
        categoriasData = categoriasResponse.data.dados;
      }
      
      let tiposData = [];
      if (tiposResponse.data.dados && Array.isArray(tiposResponse.data.dados)) {
        tiposData = tiposResponse.data.dados;
      } else if (Array.isArray(tiposResponse.data)) {
        tiposData = tiposResponse.data;
      }
      
      setCategorias(categoriasData);
      setTiposAtendimento(tiposData);
    } catch (error) {
      console.error('Erro ao buscar metadados:', error);
      setCategorias([]);
      setTiposAtendimento([]);
    }
  }, []);

  // Extrair categorias e tipos únicos dos dados das empresas (para edição)
  const updateMetadataFromEmpresas = useCallback(() => {
    if (empresas.length > 0) {
      const categoriasUnicas = [...new Set(empresas.map(emp => emp.Categoria))].filter(Boolean);
      const tiposUnicos = [...new Set(empresas.map(emp => emp['Tipo de Atendimento']))].filter(Boolean);
      
      setCategorias(categoriasUnicas.map(categoria => ({ Categoria: categoria })));
      setTiposAtendimento(tiposUnicos.map(tipo => ({ TipoAtendimento: tipo })));
    }
  }, [empresas]);

  // Atualizar metadados quando as empresas mudarem (apenas se não estiver criando nova empresa)
  useEffect(() => {
    if (!openDialog) { // Só atualiza se o dialog não estiver aberto
      updateMetadataFromEmpresas();
    }
  }, [empresas, openDialog, updateMetadataFromEmpresas]);

  useEffect(() => {
    fetchEmpresas(1);
  }, [fetchEmpresas]);

  const handlePageChange = (event, value) => {
    fetchEmpresas(value);
  };

  const handleSort = (campo) => {
    const novaOrdenacao = {
      order_by: campo,
      order_direction: ordenacao.order_by === campo && ordenacao.order_direction === 'asc' ? 'desc' : 'asc'
    };
    setOrdenacao(novaOrdenacao);
    fetchEmpresas(1, novaOrdenacao);
  };

  const handleFilterChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const aplicarFiltros = () => {
    fetchEmpresas(1);
  };

  const limparFiltros = () => {
    setFiltros({
      nomeFantasia: '',
      categoria: '',
      tipoAtendimento: '',
      dataCadastro_de: null,
      dataCadastro_ate: null
    });
    setTimeout(() => {
      fetchEmpresas(1);
    }, 100);
  };

  const handleSubmit = async () => {
    try {
      // Validar campos obrigatórios
      if (!formData.NomeFantasia || !formData.Categoria || !formData.TipoAtendimento) {
        setError('Por favor, preencha todos os campos obrigatórios.');
        return;
      }

      // Transformar os dados para o formato esperado pelo backend
      const dataToSend = {
        NomeFantasia: formData.NomeFantasia.trim(),
        CodigoEmpresa: formData.CodigoEmpresa || null,
        Categoria: formData.Categoria.trim(),
        TipoAtendimento: formData.TipoAtendimento.trim()
      };
      
      console.log('Dados sendo enviados:', dataToSend);
      
      if (editingEmpresa) {
        await api.put(`/empresas/${editingEmpresa.ID ?? editingEmpresa.id}`, dataToSend);
        setSuccess('Empresa atualizada com sucesso!');
      } else {
        await api.post('/empresas', dataToSend);
        setSuccess('Empresa adicionada com sucesso!');
      }
      
      setOpenDialog(false);
      setEditingEmpresa(null);
      resetForm();
      fetchEmpresas(paginacao.pagina_atual);
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Erro ao salvar empresa:', error);
      console.error('Resposta do servidor:', error.response?.data);
      setError(error.response?.data?.error || 'Erro ao salvar. Por favor, verifique os dados e tente novamente.');
    }
  };

  const handleEdit = async (empresa) => {
    setEditingEmpresa(empresa);
    // Garantir metadados carregados para popular os selects
    try { await fetchMetadataCompleta(); } catch (_) {}
    
    // Selecionar objetos pelo texto (independente do momento que os options chegam)
    const categoriaTexto = empresa.Categoria ?? empresa.categoria ?? '';
    const tipoTexto = empresa['Tipo de Atendimento'] ?? empresa.tipo_atendimento ?? '';
    const categoriaSelecionada = categoriaTexto ? { Categoria: categoriaTexto } : null;
    const tipoSelecionado = tipoTexto ? { TipoAtendimento: tipoTexto } : null;
    
    setFormData({
      NomeFantasia: empresa['Nome Fantasia'] ?? empresa.razao_social ?? '',
      CodigoEmpresa: (empresa.CodigoEmpresa ?? empresa.codigo_agendor ?? '').toString(),
      Categoria: empresa.Categoria ?? empresa.categoria ?? '',
      TipoAtendimento: empresa['Tipo de Atendimento'] ?? empresa.tipo_atendimento ?? '',
      categoriaSelecionada,
      tipoAtendimentoSelecionado: tipoSelecionado,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta empresa?')) {
      try {
        await api.delete(`/empresas/${id}`);
        setSuccess('Empresa excluída com sucesso!');
        fetchEmpresas(paginacao.pagina_atual);
        
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } catch (error) {
        console.error('Erro ao excluir empresa:', error);
        setError('Erro ao excluir. Por favor, tente novamente.');
      }
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({
      NomeFantasia: '',
      CodigoEmpresa: '',
      Categoria: '',
      TipoAtendimento: '',
      categoriaSelecionada: null,
      tipoAtendimentoSelecionado: null,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Backend envia ISO. Tela de empresas mostra data E horario (com segundos)
    // mantendo o mesmo padrao visual de antes: 06/05/2026 10:18:22.
    // Combina toLocaleDateString + toLocaleTimeString para evitar a virgula
    // que o {dateStyle, timeStyle} insere automaticamente em pt-BR.
    const dt = new Date(dateString);
    if (isNaN(dt.getTime())) return dateString;
    // Se vier so a data (10 chars: YYYY-MM-DD), nao adiciona horario
    if (typeof dateString === 'string' && dateString.length <= 10) {
      return dt.toLocaleDateString('pt-BR');
    }
    return `${dt.toLocaleDateString('pt-BR')} ${dt.toLocaleTimeString('pt-BR')}`;
  };

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Gerenciamento de Empresas
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Cadastre e gerencie as empresas do sistema
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterIcon />}
            >
              Filtros
            </Button>
            {isAdmin && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenCargaDialog(true)}
                startIcon={<CloudUploadIcon />}
              >
                Carga em Massa
              </Button>
            )}
            <Button
              variant="contained"
              color="success"
              onClick={async () => {
                setEditingEmpresa(null);
                resetForm();
                await fetchMetadataCompleta(); // Buscar todos os metadados para nova empresa
                setOpenDialog(true);
              }}
              startIcon={<AddIcon />}
            >
              Nova Empresa
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Painel de Filtros */}
        <Collapse in={showFilters}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filtros de Pesquisa
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Nome Fantasia"
                    value={filtros.nomeFantasia}
                    onChange={(e) => handleFilterChange('nomeFantasia', e.target.value)}
                    placeholder="Digite o nome da empresa"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Código Agendor"
                    value={filtros.codigoAgendor}
                    onChange={(e) => handleFilterChange('codigoAgendor', e.target.value)}
                    placeholder="Ex.: 43791987"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Categoria"
                    value={filtros.categoria}
                    onChange={(e) => handleFilterChange('categoria', e.target.value)}
                    placeholder="Nome da categoria"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Tipo de Atendimento"
                    value={filtros.tipoAtendimento}
                    onChange={(e) => handleFilterChange('tipoAtendimento', e.target.value)}
                    placeholder="Tipo de atendimento"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data Cadastro - De"
                      value={filtros.dataCadastro_de}
                      onChange={(newValue) => handleFilterChange('dataCadastro_de', newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data Cadastro - Até"
                      value={filtros.dataCadastro_ate}
                      onChange={(newValue) => handleFilterChange('dataCadastro_ate', newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={aplicarFiltros}
                    sx={{ height: '56px' }}
                  >
                    Aplicar Filtros
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={limparFiltros}
                    startIcon={<ClearIcon />}
                    sx={{ height: '56px' }}
                  >
                    Limpar
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>

        {/* Informações de paginação */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {empresas.length} de {paginacao.total_registros} registros
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Página {paginacao.pagina_atual} de {paginacao.total_paginas}
          </Typography>
        </Box>

        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={ordenacao.order_by === 'ID'}
                    direction={ordenacao.order_by === 'ID' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('ID')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  Código Agendor
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'NomeFantasia'}
                    direction={ordenacao.order_by === 'NomeFantasia' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('NomeFantasia')}
                  >
                    Nome Fantasia
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'Categoria'}
                    direction={ordenacao.order_by === 'Categoria' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('Categoria')}
                  >
                    Categoria
                  </TableSortLabel>
                </TableCell>
                <TableCell>Data de Cadastro</TableCell>
                <TableCell>Tipo de Atendimento</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Carregando dados...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : empresas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum registro encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                empresas.map((empresa, index) => (
                  <TableRow key={empresa.ID || empresa.id || index}>
                    <TableCell>{empresa.ID ?? empresa.id}</TableCell>
                    <TableCell>{empresa.CodigoEmpresa ?? empresa.codigo_agendor ?? '-'}</TableCell>
                    <TableCell>{empresa['Nome Fantasia'] ?? empresa.razao_social}</TableCell>
                    <TableCell>{empresa.Categoria ?? empresa.categoria}</TableCell>
                    <TableCell>{formatDate(empresa['Data de Cadastro'] ?? empresa.data_cadastro)}</TableCell>
                    <TableCell>{empresa['Tipo de Atendimento'] ?? empresa.tipo_atendimento}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(empresa)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(empresa.ID ?? empresa.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Componente de Paginação */}
        {paginacao.total_paginas > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={paginacao.total_paginas}
              page={paginacao.pagina_atual}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              disabled={loading}
            />
          </Box>
        )}

        {/* Dialog para adicionar/editar empresa */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingEmpresa ? 'Editar Empresa' : 'Nova Empresa'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Nome Fantasia"
                name="NomeFantasia"
                value={formData.NomeFantasia}
                onChange={handleChange}
                fullWidth
                required
              />

              <TextField
                label="Código Agendor"
                name="CodigoEmpresa"
                value={formData.CodigoEmpresa}
                onChange={handleChange}
                fullWidth
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />

              <Autocomplete
                fullWidth
                options={categorias}
                getOptionLabel={(option) => option.Categoria || ''}
                value={formData.categoriaSelecionada}
                onChange={(event, newValue) => {
                  setFormData({ 
                    ...formData, 
                    Categoria: newValue ? newValue.Categoria : '',
                    categoriaSelecionada: newValue 
                  });
                }}
                isOptionEqualToValue={(option, value) => option?.Categoria === value?.Categoria}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Categoria"
                    required
                  />
                )}
                noOptionsText="Nenhuma categoria encontrada"
              />

              <Autocomplete
                fullWidth
                options={tiposAtendimento}
                getOptionLabel={(option) => option.TipoAtendimento || ''}
                value={formData.tipoAtendimentoSelecionado}
                onChange={(event, newValue) => {
                  setFormData({ 
                    ...formData, 
                    TipoAtendimento: newValue ? newValue.TipoAtendimento : '',
                    tipoAtendimentoSelecionado: newValue 
                  });
                }}
                isOptionEqualToValue={(option, value) => option?.TipoAtendimento === value?.TipoAtendimento}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tipo de Atendimento"
                    required
                  />
                )}
                noOptionsText="Nenhum tipo de atendimento encontrado"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        <CargaEmpresasDialog
          open={openCargaDialog}
          onClose={() => setOpenCargaDialog(false)}
          onSuccess={() => fetchEmpresas(paginacao.pagina_atual)}
        />
      </Container>
    </Layout>
  );
};

export default EmpresasPanel; 